import { create as createProto } from '@bufbuild/protobuf';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createEnvelope } from '@/lib/protobuf/codec';
import { randomUUID } from '@/lib/uuid';
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import { CreateResearchRequestSchema } from '@/lib/protobuf/gen/research/research_pb';
import { wsManager } from '@/lib/websocket';

let createTimer: ReturnType<typeof setTimeout> | null = null;

interface ResearchCreationStoreState {
  isCreating: boolean;
  error: string | null;
  createdResearchId: string | null;
  /** The requestId used to correlate the response with our create request. */
  pendingRequestId: string | null;

  actions: {
    /**
     * workspaceId (Research-in-Workspace, E1): when non-empty the run
     * auto-files into that matter on completion. Callers resolve it via the
     * filing toggle + getWorkspaceFilingTarget; empty/omitted = personal run.
     */
    createResearch: (goal: string, profile: string, budget: number, limits?: { maxNodes: number; maxDepth: number; maxChildren: number }, workspaceId?: string) => void;
    reset: () => void;
    handleResearchResponse: (envelope: MessageEnvelope) => void;
  };
}

export const useResearchCreationStore = create<ResearchCreationStoreState>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        isCreating: false,
        error: null,
        createdResearchId: null,
        pendingRequestId: null,

        actions: {
          createResearch: (goal: string, profile: string, budget: number, limits?: { maxNodes: number; maxDepth: number; maxChildren: number }, workspaceId?: string) => {
            const requestId = randomUUID();

            set((state) => {
              state.isCreating = true;
              state.error = null;
              state.createdResearchId = null;
              state.pendingRequestId = requestId;
            });

            // Graph limits are stored for future use when the proto supports them
            if (limits) {
              console.debug('createResearch: graph limits provided', limits);
            }

            const req = createProto(CreateResearchRequestSchema, {
              goal,
              profile,
              budget,
              // Research-in-Workspace (E1): empty = personal run.
              workspaceId: workspaceId ?? '',
            });
            wsManager.sendEnvelope(
              createEnvelope({ case: 'createResearchRequest', value: req }, requestId),
            );

            if (createTimer) clearTimeout(createTimer);
            createTimer = setTimeout(() => {
              createTimer = null;
              if (get().pendingRequestId === requestId) {
                set((state) => {
                  state.isCreating = false;
                  state.error = 'Research creation timed out';
                  state.pendingRequestId = null;
                });
              }
            }, 30_000);
          },

          reset: () => {
            if (createTimer) {
              clearTimeout(createTimer);
              createTimer = null;
            }
            set((state) => {
              state.isCreating = false;
              state.error = null;
              state.createdResearchId = null;
              state.pendingRequestId = null;
            });
          },

          handleResearchResponse: (envelope: MessageEnvelope) => {
            const { pendingRequestId } = get();
            if (!pendingRequestId) return;
            if (envelope.requestId !== pendingRequestId) return;

            if (createTimer) {
              clearTimeout(createTimer);
              createTimer = null;
            }

            if (envelope.error) {
              set((state) => {
                state.isCreating = false;
                state.error = envelope.error?.message ?? 'Research creation failed';
                state.pendingRequestId = null;
              });
              return;
            }

            if (envelope.payload.case !== 'researchResponse') return;
            const research = envelope.payload.value.research;
            if (!research) return;

            set((state) => {
              state.isCreating = false;
              state.createdResearchId = research.researchId;
              state.pendingRequestId = null;
            });
          },
        },
      })),
    ),
    { name: 'ResearchCreationStore' },
  ),
);

/**
 * Register WebSocket handlers for research creation.
 * Call once on app initialization. Returns a cleanup function.
 */
export function registerResearchCreationHandlers(): () => void {
  const { actions } = useResearchCreationStore.getState();

  const unsubscribers = [
    wsManager.onMessage('researchResponse', actions.handleResearchResponse),
  ];

  return () => {
    for (const unsub of unsubscribers) {
      unsub();
    }
  };
}
